import React from 'react';
import { useQuery } from '@apollo/client';
import { Alert, Badge, Button, Card, Spinner, Table } from 'react-bootstrap';
import {
  DEPLOYMENT_STATUS_QUERY,
  DeployRun,
  DeployRunService,
  DeploymentStatusResult,
  ServiceDeployment,
} from './graphql';

/**
 * What every service in this environment is running right now.
 *
 * The environment is not a control on this screen: DNS_MAP points each environment at its
 * own admin host and the topbar dropdown keeps the path, so switching environment lands on
 * this same screen over there. The resolver reports the environment its own pod runs in.
 *
 * One line per service, and one table for both sections. Everything the wide version put on
 * its own line — workload name, branch, build number, revision, replica count — sits inline
 * behind a separator, and the two things that are long by nature (the absolute timestamps,
 * and the note explaining a null) are in the cell's tooltip. Nothing was dropped; a stack of
 * eight services just fits on one screen now, which is the point of a screen you open to
 * compare rows against each other.
 */

// A deploy that rolled pods lands both timestamps within a couple of minutes. An hour
// apart means the pipeline ran but Kubernetes had nothing to roll — the deploy succeeded
// and changed nothing, which is worth pointing at rather than leaving for someone to spot.
const UNCHANGED_THRESHOLD_MS = 60 * 60 * 1000;

/**
 * admin-client ships the same gsproxy:latest image on every deploy, so the cluster records
 * that a rollout happened but not what it shipped — the resolver can only report it as
 * unrecorded. The build that IS recorded lives in this bundle: the Jenkins admin-client
 * stage bakes REACT_APP_FE_* in at build time, which is where the topbar's "Web admin
 * revision" comes from. This screen is served by that same bundle, so it can fill its own
 * row rather than showing a blank next to a version the header is already displaying.
 *
 * Caveat worth the note in the cell: this describes the bundle the browser loaded, which a
 * hard-cached tab could have from an earlier deploy. Every other row is read from the
 * cluster and cannot drift that way.
 */
const FE_BUILD = {
  commit: (process.env.REACT_APP_FE_COMMIT || '').slice(0, 7) || null,
  branch: process.env.REACT_APP_FE_BRANCH || null,
  build: process.env.REACT_APP_FE_BUILD || null,
  source: process.env.REACT_APP_FE_SOURCE || null,
};

function withLocalBuildInfo(service: ServiceDeployment): ServiceDeployment {
  if (service.name !== 'admin-client' || !FE_BUILD.commit) {
    return service;
  }
  return {
    ...service,
    commit: FE_BUILD.commit,
    branch: FE_BUILD.branch,
    build: FE_BUILD.build,
    source: FE_BUILD.source,
    note: 'from the bundle serving this page, not from the cluster',
  };
}

const formatTime = (value: string | null) => (value ? new Date(value).toLocaleString() : '—');

function relativeAge(value: string | null): string {
  if (!value) {
    return '';
  }
  const ms = Date.now() - new Date(value).getTime();
  const minutes = Math.round(ms / 60000);
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  const hours = Math.round(minutes / 60);
  if (hours < 48) {
    return `${hours}h ago`;
  }
  return `${Math.round(hours / 24)}d ago`;
}

function changedNothing(service: ServiceDeployment): boolean {
  if (!service.startedAt || !service.liveSince) {
    return false;
  }
  const gap = Math.abs(new Date(service.startedAt).getTime() - new Date(service.liveSince).getTime());
  return gap > UNCHANGED_THRESHOLD_MS;
}

/** Hover target for text that stands in for something longer. */
const Hint = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <span title={title} style={{ cursor: 'help' }}>
    {children}
  </span>
);

/**
 * Both timestamps still matter — the rollout start is what you match against a Jenkins run,
 * and liveSince is what tells you whether that run actually replaced anything — but at full
 * `toLocaleString()` width they cost two columns. The column shows the age of the pods now
 * serving and hands over both exact times on hover.
 */
const Deployed = ({ service }: { service: ServiceDeployment }) => {
  const age = relativeAge(service.liveSince) || relativeAge(service.startedAt);
  const exact = `rollout started ${formatTime(service.startedAt)}\nserving pods live since ${formatTime(
    service.liveSince
  )}`;

  if (!age) {
    return <Hint title={exact}>—</Hint>;
  }
  return (
    <>
      <Hint title={exact}>{age}</Hint>
      {changedNothing(service) && (
        <Badge
          variant="warning"
          className="ml-1"
          title="The last deploy carried the same image, so Kubernetes rolled no pods — it succeeded and changed nothing."
        >
          unchanged
        </Badge>
      )}
    </>
  );
};

const Commit = ({ service }: { service: ServiceDeployment }) => {
  // A null commit is never a lookup that failed, so the note says which of the reasons it is.
  if (!service.commit) {
    return (
      <Hint title={service.note || 'no version is recorded anywhere for this one'}>
        <span className="text-muted font-italic">not recorded</span>
      </Hint>
    );
  }
  return (
    <>
      <code>{service.commit}</code>
      {service.branch && <span className="text-muted"> · {service.branch}</span>}
      {service.note && (
        <Hint title={service.note}>
          <span className="text-muted"> ⓘ</span>
        </Hint>
      )}
    </>
  );
};

const SourceBadge = ({ source }: { source: string | null }) => {
  if (!source) {
    return (
      <Badge variant="light" title="deployed before the pipeline stamped a source, or from outside it">
        unstamped
      </Badge>
    );
  }
  return <Badge variant={source.toLowerCase() === 'gitlab' ? 'success' : 'warning'}>{source.toLowerCase()}</Badge>;
};

const ServiceRow = ({ service }: { service: ServiceDeployment }) => (
  <tr>
    <td>
      <b>{service.name}</b>
      <span className="text-muted">
        {' '}
        {service.platform}
        {service.workload ? ` · ${service.workload}` : ''}
      </span>
    </td>
    <td>
      <Commit service={service} />
    </td>
    <td className="text-nowrap">
      <Deployed service={service} />
    </td>
    <td className="text-nowrap">
      <SourceBadge source={service.source} />
      {service.build && <span className="text-muted"> · #{service.build}</span>}
      {service.revision && <span className="text-muted"> · rev {service.revision}</span>}
      {service.ready && (
        <Hint title="ready replicas">
          <span className="text-muted"> · {service.ready}</span>
        </Hint>
      )}
    </td>
  </tr>
);

/**
 * Past runs — one row per run, one column per pipeline stage, in the order the pipeline
 * deploys them.
 *
 * A run is not recorded anywhere; the server reconstructs it by grouping the rollouts that
 * shipped the same commit (see deploymentStatus.ts). What that buys is the thing worth
 * seeing on this screen: a run where one stage did not move. Those cells are dimmed rather
 * than blank, because the service WAS running that version at the time — it just was not
 * this run that put it there.
 */
const RUN_COLUMNS = ['istio', 'api', 'background', 'admin', 'liveshow', 'admin-client'];

const formatRun = (value: string) =>
  new Date(value).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

const RunCell = ({ service }: { service?: DeployRunService }) => {
  if (!service || !service.commit) {
    return (
      <td>
        <Hint title={service?.note || 'nothing is recorded for this stage in this run'}>
          <span className="text-muted">—</span>
        </Hint>
      </td>
    );
  }
  const when = service.at ? formatTime(service.at) : 'unknown time';
  if (service.carriedOver) {
    return (
      <td>
        <Hint title={`${service.note || 'not deployed by this run'} — last rolled out ${when}`}>
          <code className="text-muted" style={{ opacity: 0.5 }}>
            {service.commit}
          </code>
        </Hint>
      </td>
    );
  }
  return (
    <td>
      <Hint title={`rolled out ${when}${service.revision ? ` · revision ${service.revision}` : ''}`}>
        <code>{service.commit}</code>
      </Hint>
    </td>
  );
};

const History = ({ runs }: { runs: DeployRun[] }) => (
  <Card className="mt-3">
    <Table responsive hover size="sm" className="mb-0 small">
      <thead>
        <tr>
          <th>Run</th>
          {RUN_COLUMNS.map((name) => (
            <th key={name}>{name}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {runs.map((run) => {
          const byName = new Map(run.services.map((service) => [service.name, service]));
          return (
            <tr key={`${run.commit}-${run.startedAt}`}>
              <td className="text-nowrap">
                <Hint title={`started ${formatTime(run.startedAt)}\nfinished ${formatTime(run.finishedAt)}`}>
                  {formatRun(run.startedAt)}
                </Hint>
                <span className="text-muted"> · </span>
                <code>{run.commit}</code>
              </td>
              {RUN_COLUMNS.map((name) => (
                <RunCell key={name} service={byName.get(name)} />
              ))}
            </tr>
          );
        })}
      </tbody>
    </Table>
  </Card>
);

const Deployments = () => {
  const { data, loading, error, refetch } = useQuery<DeploymentStatusResult>(DEPLOYMENT_STATUS_QUERY, {
    fetchPolicy: 'network-only',
  });

  if (loading) {
    return (
      <div className="p-3">
        <Spinner animation="border" size="sm" /> <span className="ml-2">Reading the cluster…</span>
      </div>
    );
  }

  // The resolver returns an empty service list when it cannot reach the Kubernetes API,
  // which in practice means the Role/RoleBinding from k8s/admin/chart is not applied yet.
  // Say that, rather than showing an empty table that looks like nothing is deployed.
  if (error) {
    return (
      <div className="p-3">
        <Alert variant="danger" className="small py-2">
          <b>Could not read the deployment status</b> — {error.message}
        </Alert>
      </div>
    );
  }

  const status = data?.deploymentStatus;
  const services = (status?.services ?? []).map(withLocalBuildInfo);
  const shared = status?.shared ?? [];

  return (
    <div className="p-3">
      <div className="d-flex align-items-baseline justify-content-between flex-wrap mb-2">
        <div>
          <h5 className="d-inline mr-2">Deployments</h5>
          <span className="small text-muted">
            {status ? (
              <>
                {status.project} · {status.namespace} · read {formatTime(status.fetchedAt)}
              </>
            ) : (
              'no data'
            )}
          </span>
        </div>
        <Button variant="outline-secondary" size="sm" onClick={() => refetch()}>
          Refresh
        </Button>
      </div>

      {services.length === 0 && (
        <Alert variant="warning" className="small py-2">
          Nothing came back. Either this admin is running outside a cluster, or the deployment-reader Role in{' '}
          <code>k8s/admin/chart</code> has not been applied to this namespace yet — the server log will show which path
          the Kubernetes API refused.
        </Alert>
      )}

      <Card>
        {/* One table for both sections: a second one would repeat the header row for two
            rows of content, and these rows are read against the pipeline rows above them. */}
        <Table responsive hover size="sm" className="mb-0 small">
          <thead>
            <tr>
              <th>Service</th>
              <th>Running</th>
              <th>Deployed</th>
              <th>Build</th>
            </tr>
          </thead>
          <tbody>
            {services.map((service) => (
              <ServiceRow key={service.name} service={service} />
            ))}

            {shared.length > 0 && (
              <tr className="bg-light">
                <td colSpan={4} className="py-1">
                  <Hint title="base is the layer api, background and admin are built on, tagged latest only. release-manager is one instance per GCP project, shared by every namespace in it. No pipeline run touches either, which is exactly why both drift unnoticed.">
                    <span className="text-muted font-italic">outside the run — no pipeline stage deploys these ⓘ</span>
                  </Hint>
                </td>
              </tr>
            )}
            {shared.map((service) => (
              <ServiceRow key={service.name} service={service} />
            ))}
          </tbody>
        </Table>
      </Card>

      {status && status.history.length > 0 && (
        <>
          <div className="d-flex align-items-baseline justify-content-between flex-wrap mt-3 mb-1">
            <h6 className="d-inline mb-0">History</h6>
            <span className="small text-muted">
              last {status.history.length} runs · dimmed = the run did not deploy it, showing what it kept
            </span>
          </div>
          <History runs={status.history} />
        </>
      )}

      <div className="small text-muted mt-2">
        Read from the Kubernetes and Cloud Run APIs, cached for 60 seconds · hover a value for the exact times and for
        why something is not recorded · switch environment from the topbar, it keeps this path
      </div>
    </div>
  );
};

export default Deployments;
